import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import type {
  IncidentsTimesSectionData,
  OutcomesSectionData,
  TaskSummarySectionData,
  TemplatesSectionData,
  UsersSectionData,
} from '@src/models/interfaces/executionsHistoryDashboard.interface';

import { getTemplateMetrics } from '../historySelectors';
import type { TasksSummaryProps } from '../types';
import type { HistorySectionState } from './useExecutionsHistorySections';
import { useSectionStatus } from './useSectionStatus';
import { useTasksDistributions } from './useTasksDistributions';
import { useTasksDrilldown } from './useTasksDrilldown';
import { useTasksKpis } from './useTasksKpis';
import { useTasksTimeMetrics } from './useTasksTimeMetrics';
import { useTasksTrendCharts } from './useTasksTrendCharts';
import { useTemplateRankings } from './useTemplateRankings';

const getSectionData = <TData>({ result }: HistorySectionState): TData | undefined =>
  result?.status === 'ok' ? (result.response.data as TData) : undefined;

/**
 * View model of the task summary. Composes one hook per section so each can change
 * (or be reused) independently; the component only renders what this returns.
 */
export const useTasksSummary = ({
  query,
  chartFilters,
  sections,
  onRetrySection,
  chartViews,
  onChartViewChange,
  ...rankingSort
}: TasksSummaryProps) => {
  const intl = useIntl();
  const toStatus = useSectionStatus(onRetrySection);

  const taskSummary = getSectionData<TaskSummarySectionData>(sections['task-summary']);
  const outcomes = getSectionData<OutcomesSectionData>(sections.outcomes);
  const incidentsTimes = getSectionData<IncidentsTimesSectionData>(sections['incidents-times']);
  const templates = getSectionData<TemplatesSectionData>(sections.templates);
  const users = getSectionData<UsersSectionData>(sections.users);
  const outcomesStatus = toStatus('outcomes', sections.outcomes);
  const incidentsStatus = toStatus('incidents-times', sections['incidents-times']);
  const usersStatus = toStatus('users', sections.users);

  const drilldown = useTasksDrilldown(query);
  const orderKpis = useMemo(
    () => ({ data: taskSummary, status: toStatus('task-summary', sections['task-summary']) }),
    [sections, taskSummary, toStatus],
  );
  const userKpis = useMemo(() => ({ data: users, status: usersStatus }), [users, usersStatus]);
  const kpis = useTasksKpis(orderKpis, userKpis, outcomes, drilldown.open);
  const trends = useTasksTrendCharts(
    chartFilters,
    chartViews,
    onChartViewChange,
    outcomes?.evolution,
    incidentsTimes?.series,
  );
  const distributions = useTasksDistributions(outcomes, users, drilldown.open);
  const timeMetrics = useTasksTimeMetrics(incidentsTimes, incidentsStatus);
  const templateMetrics = useMemo(() => getTemplateMetrics(templates?.templates ?? []), [templates]);
  const rankings = useTemplateRankings(
    templateMetrics,
    rankingSort,
    toStatus('templates', sections.templates),
  );
  const warnings = sections['task-summary'].result?.status === 'ok'
    ? sections['task-summary'].result.response.warnings
    : undefined;

  const section = (id: string) => ({
    title: intl.formatMessage({ id }),
    description: intl.formatMessage({ id: `${id}.detail` }),
  });

  return {
    kpis,
    evolution: {
      ...section('dashboards.tasks.summary.complianceEvolution'),
      ...trends.evolution,
      status: outcomesStatus,
    },
    incidents: {
      ...section('dashboards.tasks.summary.executionIncidents'),
      ...trends.incidents,
      status: incidentsStatus,
    },
    periodResult: {
      ...section('dashboards.tasks.summary.periodResult'),
      ...distributions.periodResult,
      status: outcomesStatus,
    },
    taskCompliance: {
      ...section('dashboards.tasks.summary.taskCompliance'),
      items: distributions.taskCompliance,
      status: outcomesStatus,
    },
    userDistribution: {
      ...section('dashboards.tasks.summary.userDistribution'),
      items: distributions.userDistribution,
      status: usersStatus,
    },
    warnings,
    timeMetrics,
    rankings,
    drilldown: {
      drawer: drilldown.drawer,
      table: drilldown.table,
      loading: drilldown.loading,
      status: drilldown.status,
      pagination: drilldown.pagination,
    },
  };
};
